import { Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import AuthSplitLayout from 'app/pages/auth/shared/AuthSplitLayout';
import AuthButton from 'app/pages/auth/shared/AuthButton';
import { authColors, authFontFamily, authType } from 'app/pages/auth/shared/authTheme';
import { sentryMask } from 'sentryReplayPrivacy';

export interface SsoAccountMismatchScreenProps {
  typedEmail: string;
  authenticatedIdentity: string;
  /** Web escalation: a switch was attempted and the same account returned. */
  repeat: boolean;
  /** Set once a choice was made and we are navigating away ('microsoft' |
   * 'login'). This screen is the only thing covering the signed-in app, so it
   * must stay up until the navigation lands — otherwise the dashboard flashes
   * between here and Microsoft. */
  leavingFor?: string | null;
  onContinue: () => void;
  onUseDifferentAccount: () => void;
  onSignOutOfMicrosoft: () => void;
  onBackToLogin: () => void;
}

// Same body/identity treatment as the SSO rejection screen in Login.js.
const Body = styled('p')({
  ...authType.body,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textAlign: 'center',
  width: '100%',
  margin: 0,
});

const IdentityEmphasis = styled('strong')({
  display: 'block',
  color: authColors.textStrong,
  fontWeight: 700,
  overflowWrap: 'anywhere',
  margin: '6px 0',
});

/**
 * SEE-1350: shown when Microsoft authenticated a different account than the
 * one the user typed (a cached Microsoft/Cognito session answered). Rendered
 * as a full-screen overlay above the app while the session is held at the
 * gate, using the same split auth layout as the login/rejection screens. The
 * `repeat` variant is the web-only escalation: a switch attempt came back as
 * the same wrong account, so the Entra session must be ended from Microsoft's
 * own sign-out page first.
 */
function SsoAccountMismatchScreen({
  typedEmail,
  authenticatedIdentity,
  repeat,
  leavingFor,
  onContinue,
  onUseDifferentAccount,
  onSignOutOfMicrosoft,
  onBackToLogin,
}: Readonly<SsoAccountMismatchScreenProps>): JSX.Element {
  const { t } = useTranslation();

  if (leavingFor) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, bgcolor: '#fff', overflow: 'auto' }}>
        <AuthSplitLayout
          title={
            leavingFor === 'login'
              ? t('ssoMismatch.leavingForLogin', 'Signing you out…')
              : t('ssoMismatch.leavingForMicrosoft', 'Taking you to Microsoft…')
          }
        >
          <CircularProgress size={28} sx={{ color: authColors.primary }} />
        </AuthSplitLayout>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, bgcolor: '#fff', overflow: 'auto' }}>
      <AuthSplitLayout
        title={
          repeat
            ? t('ssoMismatch.repeatTitle', "You're still signed in as a different account")
            : t('ssoMismatch.title', 'This device is signed in to Microsoft as a different account')
        }
      >
        {repeat ? (
          <Body>
            {t('ssoMismatch.repeatBody1', 'Microsoft signed you in as')}
            <IdentityEmphasis {...sentryMask}>{authenticatedIdentity}</IdentityEmphasis>
            {t(
              'ssoMismatch.repeatBody2',
              'again. To switch accounts, sign out of Microsoft first, then try again.'
            )}
          </Body>
        ) : (
          <Body>
            {t('ssoMismatch.body1', 'You entered')}
            <IdentityEmphasis {...sentryMask}>{typedEmail}</IdentityEmphasis>
            {t('ssoMismatch.body2', 'but your browser is already signed in to Microsoft as')}
            <IdentityEmphasis {...sentryMask}>{authenticatedIdentity}</IdentityEmphasis>
          </Body>
        )}

        {repeat ? (
          <>
            <AuthButton type="button" onClick={onSignOutOfMicrosoft}>
              {t('ssoMismatch.signOutOfMicrosoft', 'Sign out of Microsoft')}
            </AuthButton>
            <AuthButton type="button" variant="outlined" onClick={onUseDifferentAccount}>
              {t('ssoMismatch.signedOutTryAgain', "I've signed out — try again")}
            </AuthButton>
          </>
        ) : (
          <>
            <AuthButton type="button" onClick={onContinue}>
              {t('ssoMismatch.continueButton', 'Continue')}
            </AuthButton>
            <AuthButton type="button" variant="outlined" onClick={onUseDifferentAccount}>
              {t('ssoMismatch.useDifferentAccount', 'Use a different Microsoft account')}
            </AuthButton>
          </>
        )}
        <AuthButton type="button" variant="text" onClick={onBackToLogin}>
          {t('ssoMismatch.backToLogin', 'Back to login')}
        </AuthButton>
      </AuthSplitLayout>
    </Box>
  );
}

export default SsoAccountMismatchScreen;
